/**
 * TellCarousel - Video evidence carousel grouped by tell type
 *
 * Shows all videos containing a specific tell in a horizontal scrollable carousel.
 * Designed for hitting coaches and players to quickly review tell patterns.
 *
 * Features:
 * - Horizontal scroll with touch/swipe support
 * - Video thumbnails with tell metrics overlay
 * - Click to expand to full video viewer
 * - Filter by tell category
 * - Mobile-friendly touch gestures
 */

import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Play,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  X,
  Maximize2,
  Video,
  TrendingUp,
} from 'lucide-react';
import {
  getConfidenceLevel,
  getConfidenceColor,
  PITCH_TYPE_LABELS
} from '../../lib/wireTypes';
import {
  groupVideosByTell,
  getTellGroupSummary,
  getVideoUrl,
} from '../../lib/wireDataLoader';
import { TellMetricsOverlay, TellBadge, TellConfidenceBar } from './TellMetricsOverlay';
import VideoViewerWithMarkers from './VideoViewerWithMarkers';

// =============================================================================
// TELL SECTION HEADER
// =============================================================================

function TellSectionHeader({ tell, videoCount, avgConfidence, pitchTypesAffected, isExpanded, onToggle }) {
  const level = getConfidenceLevel(avgConfidence);
  const color = getConfidenceColor(level);

  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 bg-zinc-900/80 hover:bg-zinc-800/80 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-all group"
    >
      <div className="flex items-center gap-3">
        {/* Confidence indicator */}
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: color }}
        />

        {/* Tell name */}
        <div className="text-left">
          <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors">
            {tell}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            {/* Video count */}
            <span className="text-xs text-zinc-400">
              {videoCount} video{videoCount !== 1 ? 's' : ''}
            </span>

            {/* Pitch types affected */}
            <div className="flex gap-1">
              {pitchTypesAffected.slice(0, 3).map(pt => (
                <span
                  key={pt}
                  className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 rounded text-[10px] font-mono"
                >
                  {pt}
                </span>
              ))}
              {pitchTypesAffected.length > 3 && (
                <span className="text-[10px] text-zinc-500">
                  +{pitchTypesAffected.length - 3}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Confidence */}
        <div className="text-right">
          <span
            className="text-lg font-mono font-bold"
            style={{ color }}
          >
            {Math.round(avgConfidence * 100)}%
          </span>
          <div className="text-[10px] text-zinc-500">avg confidence</div>
        </div>

        {/* Expand/collapse indicator */}
        <ChevronRight
          className={`w-5 h-5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
      </div>
    </button>
  );
}

// =============================================================================
// VIDEO PREVIEW CARD
// =============================================================================

function VideoPreviewCard({ video, onClick }) {
  const videoRef = useRef(null);
  const [thumbnailReady, setThumbnailReady] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);

  const videoUrl = video.gcsUri ? getVideoUrl(video.gcsUri) : null;
  const pitchType = video.pitchTypeGroundTruth || video.pitchType || 'UNK';
  const tellConfidence = video.tellConfidence || video.confidence || 0;
  const level = getConfidenceLevel(tellConfidence);
  const color = getConfidenceColor(level);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !videoUrl) return;

    const handleLoadedMetadata = () => {
      videoEl.currentTime = 0.5;
    };

    const handleSeeked = () => {
      setThumbnailReady(true);
    };

    const handleError = () => {
      setThumbnailError(true);
    };

    videoEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoEl.addEventListener('seeked', handleSeeked);
    videoEl.addEventListener('error', handleError);

    return () => {
      videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoEl.removeEventListener('seeked', handleSeeked);
      videoEl.removeEventListener('error', handleError);
    };
  }, [videoUrl]);

  return (
    <button
      onClick={onClick}
      className="group flex-shrink-0 w-[200px] bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all hover:scale-[1.02]"
    >
      {/* Video Thumbnail */}
      <div className="aspect-video bg-zinc-800 relative flex items-center justify-center overflow-hidden">
        {videoUrl && !thumbnailError && (
          <video
            ref={videoRef}
            src={videoUrl}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              thumbnailReady ? 'opacity-100' : 'opacity-0'
            }`}
            muted
            playsInline
            preload="metadata"
          />
        )}

        {(!thumbnailReady || thumbnailError) && (
          <div className="absolute inset-0 bg-zinc-800 flex items-center justify-center">
            <Video className="w-8 h-8 text-zinc-600" />
          </div>
        )}

        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
          </div>
        </div>

        {/* Pitch type badge */}
        <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 backdrop-blur rounded text-xs font-mono text-white">
          {pitchType}
        </div>

        {/* Confidence indicator */}
        <div
          className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: color }}
          title={`${Math.round(tellConfidence * 100)}% confidence`}
        />

        {/* Timestamp badge if available */}
        {video.tellTimestamp && (
          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 backdrop-blur rounded text-[10px] font-mono text-white/80">
            {video.tellTimestamp}
          </div>
        )}
      </div>

      {/* Card footer */}
      <div className="p-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-zinc-400">
            {video.gameDate || 'Unknown date'}
          </span>
          <span
            className="text-[11px] font-mono font-bold"
            style={{ color }}
          >
            {Math.round(tellConfidence * 100)}%
          </span>
        </div>
        {video.tellDescription && (
          <p className="text-[10px] text-zinc-500 mt-1 line-clamp-2">
            {video.tellDescription}
          </p>
        )}
      </div>
    </button>
  );
}

// =============================================================================
// HORIZONTAL CAROUSEL
// =============================================================================

function HorizontalCarousel({ videos, onVideoSelect }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, [videos]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 420; // ~2 cards
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  if (!videos || videos.length === 0) {
    return (
      <div className="text-center py-6 text-zinc-500">
        No videos available for this tell
      </div>
    );
  }

  return (
    <div className="relative group/carousel">
      {/* Left scroll button */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-zinc-900/90 hover:bg-zinc-800 rounded-full flex items-center justify-center border border-zinc-700 opacity-0 group-hover/carousel:opacity-100 transition-opacity"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide py-2 px-1"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {videos.map((video, idx) => (
          <VideoPreviewCard
            key={video.videoUid || idx}
            video={video}
            onClick={() => onVideoSelect(video)}
          />
        ))}
      </div>

      {/* Right scroll button */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-zinc-900/90 hover:bg-zinc-800 rounded-full flex items-center justify-center border border-zinc-700 opacity-0 group-hover/carousel:opacity-100 transition-opacity"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-zinc-950 to-transparent pointer-events-none opacity-0 group-hover/carousel:opacity-100 transition-opacity" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-zinc-950 to-transparent pointer-events-none opacity-0 group-hover/carousel:opacity-100 transition-opacity" />
    </div>
  );
}

// =============================================================================
// MAIN TELL CAROUSEL COMPONENT
// =============================================================================

/**
 * TellCarousel Component
 * @param {Object} props
 * @param {Array} props.analyses - Array of VideoAnalysis objects
 * @param {string} [props.pitcherName] - Pitcher name for context
 * @param {Function} [props.onReanalyze] - Re-analysis callback
 * @param {Function} [props.onAnalysisUpdated] - Called when analysis is updated
 * @param {string} [props.className]
 */
export function TellCarousel({
  analyses,
  pitcherName,
  onReanalyze,
  onAnalysisUpdated,
  className = '',
}) {
  const [expandedTell, setExpandedTell] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);

  // Group videos by tell
  const groupedTells = useMemo(() => {
    return groupVideosByTell(analyses);
  }, [analyses]);

  const summary = useMemo(() => {
    return getTellGroupSummary(groupedTells);
  }, [groupedTells]);

  // Get filtered tell groups
  const tellCategories = Object.keys(groupedTells);
  const filteredCategories = filterCategory
    ? tellCategories.filter(cat => cat === filterCategory)
    : tellCategories;

  // Auto-expand first tell if only one
  useEffect(() => {
    if (tellCategories.length === 1 && !expandedTell) {
      setExpandedTell(tellCategories[0]);
    }
  }, [tellCategories, expandedTell]);

  if (tellCategories.length === 0) {
    return (
      <div className={`p-6 bg-zinc-900/50 rounded-lg border border-zinc-800 text-center ${className}`}>
        <Eye className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
        <p className="text-zinc-400">No tells detected in videos</p>
        <p className="text-xs text-zinc-500 mt-1">
          Run analysis to detect pitch tipping patterns
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            Tell Evidence by Type
          </h2>
          <span className="text-sm text-zinc-400">
            {summary.totalTellTypes} tell{summary.totalTellTypes !== 1 ? 's' : ''} •{' '}
            {summary.totalVideosWithTells} video{summary.totalVideosWithTells !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Filter dropdown */}
        {tellCategories.length > 1 && (
          <div className="relative">
            <button
              onClick={() => setFilterCategory(filterCategory ? null : tellCategories[0])}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                filterCategory
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              {filterCategory || 'All Tells'}
            </button>
            {filterCategory && (
              <button
                onClick={() => setFilterCategory(null)}
                className="ml-1 p-1 text-zinc-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tell category filters as chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tellCategories.map(category => {
          const data = groupedTells[category];
          return (
            <TellBadge
              key={category}
              category={category}
              count={data.videoCount}
              avgConfidence={data.avgConfidence}
              onClick={() => {
                setFilterCategory(filterCategory === category ? null : category);
                setExpandedTell(category);
              }}
            />
          );
        })}
      </div>

      {/* Tell sections */}
      <div className="space-y-3">
        {filteredCategories.map(category => {
          const data = groupedTells[category];
          const isExpanded = expandedTell === category;

          return (
            <div
              key={category}
              className="rounded-lg overflow-hidden"
            >
              <TellSectionHeader
                tell={data.categoryLabel || category}
                videoCount={data.videoCount}
                avgConfidence={data.avgConfidence}
                pitchTypesAffected={data.pitchTypesAffected}
                isExpanded={isExpanded}
                onToggle={() => setExpandedTell(isExpanded ? null : category)}
              />

              {/* Expanded carousel */}
              {isExpanded && (
                <div className="mt-2 p-3 bg-zinc-900/40 rounded-lg border border-zinc-800">
                  {/* Description */}
                  {data.description && (
                    <p className="text-sm text-zinc-300 mb-3 border-l-2 border-zinc-700 pl-3">
                      {data.description}
                    </p>
                  )}

                  {/* Video carousel */}
                  <HorizontalCarousel
                    videos={data.videos}
                    onVideoSelect={setSelectedVideo}
                  />

                  {/* Quick stats */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-800 text-xs text-zinc-500">
                    <span>
                      Affects: {data.pitchTypesAffected.map(pt => PITCH_TYPE_LABELS[pt] || pt).join(', ')}
                    </span>
                    <span>
                      Occurrences: {data.totalOccurrences}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-sm text-zinc-400">
                  {selectedVideo.pitchTypeGroundTruth || selectedVideo.pitchType} • {selectedVideo.gameDate}
                </span>
                {selectedVideo.tellTimestamp && (
                  <span className="text-xs text-zinc-500 font-mono">
                    Tell @ {selectedVideo.tellTimestamp}
                  </span>
                )}
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <VideoViewerWithMarkers
              analysis={selectedVideo}
              onClose={() => setSelectedVideo(null)}
              onReanalyze={onReanalyze}
              onAnalysisUpdated={onAnalysisUpdated}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TellCarousel;
